import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { PropTypes } from 'prop-types';
import { useField } from 'formik';
import classnames from 'classnames';
import styled from 'styled-components';

import Asterisk from '../Asterisk';
import MagnifyingGlassIcon from '@icons/MagnifyingGlassIcon';
import LoadingInline from '@molecules/LoadingInline';
import { ButtonNoStyles, LabelLarge } from '@styled';
import colors from '@general/colors';
import CloseIcon from '@icons/CloseIcon';

const AutocompleteContainerStyled = styled.div`
    display: flex;
    flex: 1 1 100%;
    position: relative;
    border: 1px solid #ccc;
    border-radius: 5px;
    overflow: hidden;
`;
const InputContainerStyled = styled.div`
    align-items: center;
    display: flex;
    position: relative;
    flex: 1;
`;

const InputStyled = styled.input`
    display: flex;
`;

const ClearContainerStyled = styled.div`
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 1) 35%, rgba(255, 255, 255, 1));
    display: flex;
    position: absolute;
    right: 1rem;
`;

const CloseButtonStyled = styled(ButtonNoStyles)``;

const EditAddressButtonStyled = styled(ButtonNoStyles)`
    border-bottom: 1px solid ${colors.darkGray};
    color: ${colors.darkGray};
    cursor: pointer;

    &:hover,
    &:focus {
        color: ${colors.azure};
        border-bottom: 1px solid ${colors.azure};
    }
`;

const SearchIconContainer = styled.div`
    border-top: 1px solid ${colors.lightGray};
    border-right: 1px solid ${colors.lightGray};
    border-bottom: 1px solid ${colors.lightGray};
    background-color: ${colors.azure};
    display: flex;
    padding: 1rem;
`;

const DropdownList = styled.ul`
    margin-top: -34px;
    position: absolute;
    width: 100%;
    z-index: 999;
`;

const StyledEditAddressContainer = styled.div`
    display: flex;
    flex-basis: 100%;
    margin-top: 11px;
`;

const AddressAutocomplete = ({
    label,
    isLoading,
    styles,
    onAddressClear,
    onAddressSelect,
    onBlur,
    onShowExpandedAddressFields = () => {},
    showEditAddress,
    ...props
}) => {
    const [field, meta, { setError, setTouched, setValue }] = useField(props);
    const [mapboxResults, setMapboxResults] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState('');
    const [showResults, setShowResults] = useState(false);

    const [userProximity, setUserProximity] = useState('ip');
    const isEcommUrl =
        window.location.pathname.includes('cart') ||
        window.location.pathname.includes('event-registration') ||
        window.location.pathname.includes('membership');

    const fieldRef = useRef(null);
    const { id, name, required } = props;

    useEffect(() => {
        // Set the selectedAddress to the value on component load
        if (!meta.touched && meta.value) {
            setSelectedAddress(meta.value);
        }
    }, [meta.touched, meta.value]);

    // Only use geolocation in ecomm section for now
    useEffect(() => {
        if (isEcommUrl) {
            if ('geolocation' in navigator) {
                navigator.geolocation.getCurrentPosition(
                    position => {
                        const { longitude, latitude } = position.coords;
                        setUserProximity([longitude, latitude].join(','));
                    },
                    error => {
                        console.error('Error getting geolocation:', error);
                    }
                );
            }
        }
    }, [isEcommUrl]);

    const parseMapboxResult = mapboxResult => {
        const cityContext = mapboxResult.context.find(context => context.id.includes('place'));
        const stateContext = mapboxResult.context.find(context => context.id.includes('region'));
        const stateCodeSplit = stateContext ? stateContext.short_code.split('-') : [];
        const countryContext = mapboxResult.context.find(context => context.id.includes('country'));
        const zipContext = mapboxResult.context.find(context => context.id.includes('postcode'));

        return {
            address1: `${mapboxResult.address ? `${mapboxResult.address} ` : ''}${mapboxResult.text}`,
            city: cityContext ? cityContext.text : null,
            state: stateCodeSplit.length > 0 ? stateCodeSplit[stateCodeSplit.length - 1] : null,
            country: countryContext ? countryContext.short_code.toUpperCase() : null,
            zip: zipContext ? zipContext.text : null,
        };
    };

    const updateQuery = event => {
        if (event.target.value === '') {
            onAddressClear();
            setError('');
            onShowExpandedAddressFields(true);
        }

        setValue(event.target.value);
        const query = encodeURI(event.target.value);
        const mapboxQueryUrl = isEcommUrl
            ? `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?proximity=${userProximity}&types=address&access_token=${process.env.MAPBOX_API_KEY}`
            : `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?types=address&access_token=${process.env.MAPBOX_API_KEY}`;
        axios.get(mapboxQueryUrl).then(res => {
            const { features } = res.data;
            setMapboxResults(features);
            setShowResults(true);
        });
    };

    const handleSelectedAddress = mapboxResult => {
        onShowExpandedAddressFields(false);
        setValue(mapboxResult.place_name);

        const previouslySelectedAddress = selectedAddress;

        // Set newly selected address
        if (previouslySelectedAddress !== mapboxResult.place_name) {
            setSelectedAddress(mapboxResult.place_name);
        }

        if (onAddressSelect) {
            onAddressSelect(parseMapboxResult(mapboxResult));
        }

        setMapboxResults([]);
    };

    const onKeyDown = e => {
        // tab
        if (e.keyCode === 9) {
            if (!meta.value || meta.value !== selectedAddress) {
                setError('');
                clearMapboxResults();
                onShowExpandedAddressFields(true);
            } else {
                onShowExpandedAddressFields(false);
            }
        }

        // escape
        if (e.keyCode === 27) {
            setShowResults(false);
        }
    };

    const clearMapboxResults = () => {
        setMapboxResults([]);
        setShowResults(false);
    };

    const handleBlur = () => {
        if (!meta.value || meta.value !== selectedAddress) {
            onShowExpandedAddressFields(true);
            setError('');
        } else {
            onShowExpandedAddressFields(false);
        }
    };

    const hasError = meta.touched && meta.error;

    return (
        <>
            <div className={classnames('form-group', { 'has-danger': hasError })}>
                <LabelLarge className="form-control-label" htmlFor={id || name}>
                    {label} {required && <Asterisk />}
                </LabelLarge>

                {isLoading && <LoadingInline />}
                {!isLoading && (
                    <>
                        <AutocompleteContainerStyled>
                            <InputContainerStyled>
                                <InputStyled
                                    autoComplete="off"
                                    className={classnames('form-control', 'p-3', { 'form-control-danger': hasError })}
                                    {...field}
                                    {...props}
                                    ref={fieldRef}
                                    onChange={e => {
                                        setTouched(true);
                                        updateQuery(e);
                                    }}
                                    onBlur={() => {
                                        setTouched(true);
                                        handleBlur();
                                    }}
                                    onKeyDown={onKeyDown}
                                    style={styles}
                                />
                                {meta.value && (
                                    <ClearContainerStyled>
                                        <CloseButtonStyled
                                            onClick={() => {
                                                onAddressClear();
                                                setValue('');
                                                clearMapboxResults();
                                                onShowExpandedAddressFields(false);
                                                fieldRef.current.focus();
                                            }}
                                        >
                                            <CloseIcon fill={colors.azure} height="11px" width="11px" />
                                        </CloseButtonStyled>
                                    </ClearContainerStyled>
                                )}
                            </InputContainerStyled>
                            <SearchIconContainer>
                                <MagnifyingGlassIcon size={20} />
                            </SearchIconContainer>
                        </AutocompleteContainerStyled>
                        {hasError ? <div className="error-msg">{meta.error}</div> : null}
                        {showEditAddress && (
                            <StyledEditAddressContainer>
                                <EditAddressButtonStyled
                                    onClick={e => {
                                        e.preventDefault();
                                        setError('');
                                        clearMapboxResults();
                                        onShowExpandedAddressFields(true);
                                    }}
                                >
                                    Enter Address Manually
                                </EditAddressButtonStyled>
                            </StyledEditAddressContainer>
                        )}
                        {showResults && (
                            <div className="relative">
                                <DropdownList className="list-group">
                                    {mapboxResults.map((result, key) => (
                                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
                                        <li
                                            key={key}
                                            onClick={e => {
                                                e.preventDefault();
                                                handleSelectedAddress(result);
                                            }}
                                            className="list-group-item cursor-pointer"
                                        >
                                            <a href="#">{result.place_name}</a>
                                        </li>
                                    ))}
                                </DropdownList>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

AddressAutocomplete.propTypes = {
    label: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    required: PropTypes.bool,
    isLoading: PropTypes.bool,
    showEditAddress: PropTypes.bool,
    styles: PropTypes.object,
    onAddressClear: PropTypes.func,
    onAddressSelect: PropTypes.func,
    onShowExpandedAddressFields: PropTypes.func,
    onBlur: PropTypes.func,
};

export default AddressAutocomplete;
